import type { TFunction } from 'react-i18next'

import type { PaymentData } from '~/constants/addPaymentDialogV2Constants'
import { PaymentFlowState } from '~/constants/addPaymentDialogV2Constants'
import type { InvoiceOrEstimate } from '~/types'

interface InitiateTerminalSaleDependencies {
  error: any
  initiateTerminalSaleMutation: (options: {
    variables: {
      input: {
        amount: number
        externalDeviceId: string
        invoiceIds: string[]
      }
    }
  }) => Promise<any>
  invoice: InvoiceOrEstimate
  paymentDialogDispatch: React.Dispatch<any>
  setIsButtonActionLoading: (loading: boolean) => void
  t: TFunction<'Dialogs'>
}

/**
 * Handles the terminal sale mutation with error handling.
 * Extracted to reduce cognitive complexity in useMemo.
 */
export const initiateTerminalSale = async (
  currentPaymentData: PaymentData,
  deps: InitiateTerminalSaleDependencies,
): Promise<void> => {
  const {
    paymentDialogDispatch,
    setIsButtonActionLoading,
    invoice,
    initiateTerminalSaleMutation,
    error,
    t,
  } = deps

  try {
    const result = await initiateTerminalSaleMutation({
      variables: {
        input: {
          amount: parseFloat(currentPaymentData.amountToCharge || '0'),
          externalDeviceId: currentPaymentData.paymentTerminal || '',
          invoiceIds: invoice.id.split(','),
        },
      },
    })

    if (!result.data?.initiateTerminalSale || error) {
      paymentDialogDispatch({
        type: 'CANCEL_TRANSACTION',
      })
      paymentDialogDispatch({
        type: 'SET_PAYMENT_ERROR',
        payload: {
          message: t(
            'Dialogs:ADD_PAYMENT_DIALOG_V2.FAILED_TO_INITIATE_TERMINAL_SALE',
          ),
          severity: 'error',
          status: 500,
        },
      })
    } else {
      paymentDialogDispatch({
        type: 'SET_STATE',
        payload: PaymentFlowState.PAYMENT_REQUEST,
      })
      paymentDialogDispatch({
        type: 'SET_PAYMENT_DATA',
        payload: {
          ...currentPaymentData,
          transactionId: result.data.initiateTerminalSale.id,
        },
      })
    }
  } catch (err) {
    paymentDialogDispatch({
      type: 'CANCEL_TRANSACTION',
    })
    paymentDialogDispatch({
      type: 'SET_PAYMENT_ERROR',
      payload: {
        message: t(
          'Dialogs:ADD_PAYMENT_DIALOG_V2.FAILED_TO_INITIATE_TERMINAL_SALE',
        ),
        severity: 'error',
        status: 500,
      },
    })
  } finally {
    setIsButtonActionLoading(false)
  }
}
